import {
  FirestoreCollections,
  Goal,
  IconFormNames,
  ID,
  LinkedItemSection,
  Roles,
  SortingMode,
  Task,
  Timestamp,
} from '@griply/types';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';

export type TransformLifeAreaSectionSource = {
  id: ID;
  sections: LinkedItemSection[] | null;
  color?: string | null;
  roles: Roles;
};

export type TransformLifeAreaSectionToGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  sectionId: ID;
  lifeArea: TransformLifeAreaSectionSource;
  // The section's tasks, resolved by the caller (each app fetches with its SDK).
  tasks: Task[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch: WriteBatchFn<TDocRef, TDb>;
  onWriteError: (error: unknown) => void;
  // The name a section without one gives the goal: firestore.rules refuse a goal without a name.
  untitledGoalName: string;
};

// Promotes a life-area section into a standalone (root) goal that lives inside
// the same life area: a new goal inherits the section's tasks, those tasks are
// re-pointed to it (keeping their life area), and the section is removed from
// the life area. Life areas don't track task counts, so none are adjusted.
export const transformLifeAreaSectionToGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: TransformLifeAreaSectionToGoalOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TDb
  >,
): Promise<ID | undefined> => {
  const {
    sectionId,
    lifeArea,
    tasks,
    collection,
    doc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
    untitledGoalName,
  } = options;

  const section = lifeArea.sections?.find(({ id }) => id === sectionId);
  if (!section) {
    return undefined;
  }

  const batchDocuments = new BatchDocuments<TDocRef, TDb>(db, writeBatch);
  const goalCollection = collection(db, FirestoreCollections.Goals);
  const lifeAreaCollection = collection(db, FirestoreCollections.LifeAreas);
  const taskCollection = collection(db, FirestoreCollections.Tasks);

  const completedTasks = tasks.filter(({ completedAt }) => !!completedAt);

  const newGoalRef = doc(goalCollection);
  const newGoal: Goal = {
    id: newGoalRef.id,
    name: section.name.trim() || untitledGoalName,
    taskCount: tasks.length,
    completedTaskCount: completedTasks.length,
    color: lifeArea.color ?? null,
    lifeAreaId: lifeArea.id,
    startDate: null,
    deadline: null,
    iconName: IconFormNames.Target,
    image: null,
    sections: null,
    goalSorting: null,
    metric: null,
    taskSorting: section.tasks,
    roles: lifeArea.roles,
    ancestorIds: null,
    sortingMode: SortingMode.Custom,
    taskSortingMode: SortingMode.Custom,
    metadata: null,
    excludedFromLimits: null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    createdAt: serverTimestamp() as unknown as Timestamp,
    updatedAt: serverTimestamp() as unknown as Timestamp,
  };
  batchDocuments.addDocument({ ref: newGoalRef, data: newGoal });

  // Tasks keep their life area (the new goal lives in it) and gain the goalId.
  tasks.forEach((task) => {
    batchDocuments.updateDocument({
      ref: doc(taskCollection, task.id),
      data: {
        goalIds: [newGoal.id],
        goalId: newGoal.id,
        updatedAt: serverTimestamp(),
      },
    });
  });

  batchDocuments.updateDocument({
    ref: doc(lifeAreaCollection, lifeArea.id),
    data: {
      sections:
        lifeArea.sections?.filter((section) => section.id !== sectionId) ??
        null,
      updatedAt: serverTimestamp(),
    },
  });

  detachWrite(batchDocuments.commit(), onWriteError);

  return newGoalRef.id;
};
